import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import ProfileSummaryCard from '../components/freelancers/ProfileSummaryCard';
import ProfileAlliancesSection from '../components/freelancers/ProfileAlliancesSection';
import ProfileReviewsSection from '../components/freelancers/ProfileReviewsSection';
import ProfileAchievementsSection from '../components/freelancers/ProfileAchievementsSection';
import ProfileProjectHistorySection from '../components/freelancers/ProfileProjectHistorySection';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { NotFound } from '../components/common/NotFound';
import { useUIStore } from '../stores/uiStore';
import { useReviewStore } from '../stores/reviewStore';
import { useFreelancerProfileStore } from '../stores/freelancerProfileStore';
import ProfileServicesSection from '../components/freelancers/ProfileServicesSection';

const isNonEmpty = (s?: string | null) => typeof s === 'string' && s.trim().length > 0;

const FreelancerProfilePage = () => {
  const { freelancerId } = useParams<{ freelancerId: string }>();
  const { t } = useTranslation(['profile', 'common']);

  const {
    fetch_freelancer_profile,
    loading: profileLoading,
    freelancerProfiles,
    fetchActiveAlliances,
    freelancerAlliances,
    fetchMoreActiveAlliances,
  } = useFreelancerProfileStore();

  const { fetchReviews, userReviews, fetchMoreReviews } = useReviewStore();

  const { setActiveBreadCrumb } = useUIStore();

  const {
    freelancerProjects: profileProjects,
    fetchProfileProjects,
    fetchMoreProjects,
  } = useFreelancerProfileStore();

  useEffect(() => {
    fetch_freelancer_profile(freelancerId!);

    fetchReviews({ userId: freelancerId! });

    fetchActiveAlliances({ freelancerId: freelancerId! });

    fetchProfileProjects({ freelancerId: freelancerId! });
  }, [
    freelancerId,
    fetchActiveAlliances,
    fetchProfileProjects,
    fetchReviews,
    fetch_freelancer_profile,
  ]);

  const profile = freelancerId ? freelancerProfiles[freelancerId] : null;
  const userReview = freelancerId ? userReviews[freelancerId] : null;
  const userAlliances = freelancerId ? freelancerAlliances[freelancerId] : null;

  const alliances = userAlliances?.alliances || [];
  const hasMoreAlliances = userAlliances?.hasMore || false;
  const allianceLoading = userAlliances?.loading || false;

  const reviews = userReview?.reviews || [];
  const hasMoreReviews = userReview?.hasMore || false;
  const reviewLoading = userReview?.loading || false;

  const currentProjectsBucket = freelancerId ? profileProjects[freelancerId] : undefined;
  const projectLoading = currentProjectsBucket?.loading ?? false;
  const projects = currentProjectsBucket?.projects ?? [];
  const hasMoreProjects = currentProjectsBucket?.hasMore ?? false;

  useEffect(() => {
    if (profile?.first_name) {
      setActiveBreadCrumb(profile.first_name);
    }
  }, [profile, setActiveBreadCrumb]);

  if (profileLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isNonEmpty(freelancerId) || !profile) {
    return <NotFound title={t('profileNotFound')} subtitle={t('profileNotFoundSubtitle')} />;
  }

  return (
    <div className="flex flex-col md:min-h-[calc(100vh-2rem)] md:px-5 lg:px-32">
      <div className="flex w-full flex-col pt-4 md:w-2/3 md:px-1 md:pr-8">
        <h1 className="text-5xl font-bold text-secondary">
          {profile.first_name} {profile.last_name}
        </h1>
      </div>

      <div className="my-8">
        <ProfileSummaryCard profile={profile} />

        <ProfileServicesSection profile={profile} />
        <div className="flex flex-col">
          <ProfileAlliancesSection
            alliances={alliances}
            hasMoreAlliances={hasMoreAlliances}
            loading={allianceLoading}
            profile={profile}
            fetchMoreAlliances={() => fetchMoreActiveAlliances(freelancerId!)}
          />
        </div>

        <ProfileAchievementsSection achievements={profile.achievements} />

        <div className="flex flex-col">
          <ProfileReviewsSection
            loading={reviewLoading}
            reviews={reviews}
            fetchMoreReviews={() => fetchMoreReviews(freelancerId!)}
            hasMoreReviews={hasMoreReviews}
            freelancerStats={profile.stats}
          />

          <ProfileProjectHistorySection
            loading={projectLoading}
            projects={projects}
            hasMoreProjects={hasMoreProjects}
            fetchMoreProjects={() => fetchMoreProjects(freelancerId!)}
          />
        </div>
      </div>
    </div>
  );
};

export default FreelancerProfilePage;
